import {
  forwardRef,
  MouseEventHandler,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { twMerge } from "tailwind-merge";
import Link from "next/link";
import Spinner from "./Spinner";
import {
  BUTTON_COLOR,
  BUTTON_SIZE,
  BUTTON_STATUS,
  BUTTON_VARIANT,
  type ButtonProps,
} from "./types";
import { buttonColor } from "./constants";
import {
  resolveButtonSizeClassNames,
  resolvePrimaryButtonSize,
  spinnerSizeByButtonSize,
} from "./buttonSizeClasses";

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    variant = BUTTON_VARIANT.FILL,
    size = BUTTON_SIZE.XLARGE,
    sizes,
    color = BUTTON_COLOR.PRIMARY,
    startIcon,
    endIcon,
    className,
    children,
    href,
    isLoading,
    fullWidth,
    linkClassName,
    onClick,
    type,
    disabled,
    ...buttonDomProps
  } = props;

  const target = href && "target" in props ? props.target : undefined;

  const primarySize = resolvePrimaryButtonSize(size, sizes);

  const buttonStatusValues = Object.values(BUTTON_STATUS);

  const buttonColorClassNames = buttonStatusValues.reduce((prev, status) => {
    const colorSchema = buttonColor[color][variant][status];
    const background = colorSchema.bg.length ? colorSchema.bg : "";
    const border = colorSchema.border.length ? colorSchema.border : "";
    const text = colorSchema.text.length ? colorSchema.text : "";
    prev += `${background} ${border} ${text} `;
    return prev;
  }, "");

  const variantOverrides =
    variant === BUTTON_VARIANT.TEXT_ACTION ? "!px-0" : "";
  const classNames = twMerge(
    "flex items-center justify-center",
    resolveButtonSizeClassNames(size, sizes),
    variantOverrides,
    buttonColorClassNames,
    startIcon || endIcon ? " gap-1.5" : "",
    (startIcon || endIcon) && primarySize === BUTTON_SIZE.SMALL ? "gap-1" : "",
    className
  );

  const spinnerSize = spinnerSizeByButtonSize[primarySize] ?? 18;

  const childrenNodes = (
    <>
      {isLoading ? (
        <Spinner size={spinnerSize} />
      ) : (
        <>
          {startIcon || null}
          {children}
          {endIcon || null}
        </>
      )}
    </>
  );

  if (href) {
    const handleLinkClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
      if (disabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      onClick?.(event as unknown as ReactMouseEvent<HTMLButtonElement>);
    };

    return (
      <Link
        href={href}
        target={target || "_self"}
        className={linkClassName}
        style={{ width: fullWidth ? "100%" : "auto" }}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
        onClick={handleLinkClick}
      >
        <button
          ref={ref}
          type="button"
          className={classNames}
          disabled={disabled}
          {...buttonDomProps}
        >
          {childrenNodes}
        </button>
      </Link>
    );
  }

  return (
    <button
      ref={ref}
      className={classNames}
      type={type || "button"}
      disabled={disabled}
      onClick={onClick}
      {...buttonDomProps}
    >
      {childrenNodes}
    </button>
  );
});

Button.displayName = "Button";

export default Button;
